import { useEffect, useState } from "react";
import { useSearchParams, useLocation } from "react-router-dom";

const DEFAULT_STEP_STORAGE_KEY = "checkout-step";

interface StepValidation {
    canAccessStep: (
        step: number,
        hasItems?: boolean,
        isExpired?: boolean,
    ) => boolean;
    getValidStep: (
        requestedStep: number,
        hasItems?: boolean,
        isExpired?: boolean,
    ) => number;
}

/**
 * Validates if a user can access a specific checkout step
 * @param step - The step number to validate (0: Cart/Items, 1: Checkout with Payment)
 * @param hasRedeemers - Whether there are redeemers/travelers added
 * @param hasItems - Whether there are items in cart
 * @param isExpired - Whether any items are expired
 * @returns true if the step is accessible, false otherwise
 */
export const checkCanAccessStep = (
    step: number,
    hasItems: boolean = true,
    isExpired: boolean = false,
) => {
    if (step === 0) return true;
    if (step === 1) return hasItems && !isExpired;
    return false;
};

/**
 * Returns a valid step number based on the requested step and user permissions
 * @param requestedStep - The step number the user wants to access
 * @param hasItems - Whether there are items in cart
 * @param isExpired - Whether any items are expired
 * @returns A valid step number the user can access
 */
export const getValidStep = (
    requestedStep: number,
    hasItems: boolean = true,
    isExpired: boolean = false,
) => {
    if (requestedStep < 0 || requestedStep > 1) return 0;
    if (!hasItems || isExpired) return 0;
    if (checkCanAccessStep(requestedStep, hasItems, isExpired)) return requestedStep;
    return 0;
};

const createStepValidation = (): StepValidation => ({
    canAccessStep: checkCanAccessStep,
    getValidStep,
});

/**
 * Custom hook to manage checkout step persistence across page reloads and navigation
 * Handles step validation, URL parameters, and localStorage persistence
 * @param hasItems - Whether there are items in cart
 * @param isLoading - Whether cart data is still loading
 * @param storageKey - Optional unique key for localStorage (e.g., 'cart-step', 'quotes-step')
 * @param isExpired - Whether any items are expired
 * @returns Object containing activeStepIndex and setActiveStep function
 */
export const useStepPersistence = (
    hasItems: boolean = true,
    isLoading: boolean = false,
    storageKey: string = DEFAULT_STEP_STORAGE_KEY,
    isExpired: boolean = false,
) => {
    const [searchParams] = useSearchParams();
    const location = useLocation();
    const validation = createStepValidation();
    const [hasInitialized, setHasInitialized] = useState(false);

    const stepParam = searchParams.get("step");
    const storedRaw = localStorage.getItem(storageKey);
    const storedStep = storedRaw !== null ? Number(storedRaw) : undefined;

    const initialStep = (() => {
        if (stepParam) {
            const urlStep = parseInt(stepParam, 10);
            if (!isNaN(urlStep)) return urlStep;
        }
        if (typeof storedStep === "number" && !isNaN(storedStep)) return storedStep;
        return 0;
    })();

    const [activeStepIndex, setActiveStepInternal] = useState(initialStep);

    // setActiveStep accepts optional override values to avoid stale closure issues
    // when called after async operations like saving cart items
    const setActiveStep = (
        step: number,
        overrides?: { hasItems?: boolean; isExpired?: boolean },
    ) => {
        const effectiveHasItems = overrides?.hasItems ?? hasItems;
        const effectiveIsExpired = overrides?.isExpired ?? isExpired;
        setActiveStepInternal(
            validation.getValidStep(
                step,
                effectiveHasItems,
                effectiveIsExpired,
            ),
        );
    };

    useEffect(() => {
        if (!hasInitialized && !isLoading) {
            setHasInitialized(true);
        }
        // Don't validate while loading
        if (isLoading || !hasInitialized) return;

        // Only change step if current step is NOT accessible
        const canAccess = validation.canAccessStep(
            activeStepIndex,
            hasItems,
            isExpired,
        );
        if (!canAccess) {
            const validStep = validation.getValidStep(
                activeStepIndex,
                hasItems,
                isExpired,
            );
            if (validStep !== activeStepIndex) {
                setActiveStepInternal(validStep);
            }
        }
    }, [hasItems, isLoading, hasInitialized, isExpired, activeStepIndex]);

    useEffect(() => {
        localStorage.setItem(storageKey, String(activeStepIndex));
    }, [activeStepIndex, storageKey]);

    useEffect(() => {
        return () => {
            if (!location.pathname.includes("cart") && !location.pathname.includes("checkout")) {
                localStorage.removeItem(storageKey);
            }
        };
    }, [location.pathname, storageKey]);

    return { activeStepIndex, setActiveStep };
};
